/**
 * TCG Data Service with 12-hour caching
 * Fetches comprehensive TCG data from the API
 */

import {publicAPI, tcgAPI} from './api';
import dataCache from './dataCache';

class TCGDataService {
    /**
     * Fetch comprehensive data for all TCGs
     * Uses cache if valid, otherwise fetches fresh data
     * Now uses batch endpoint (1 API call instead of N+1)
     */
    async getTCGStats(force = false) {
        // Check fresh cache first
        if (!force) {
            const cached = dataCache.getCachedData(dataCache.cacheKeys.TCG_STATS);
            if (cached) {
                console.log('📦 Using cached TCG stats');
                return cached;
            }
        }

        // Stale-while-revalidate: return stale data immediately, refresh in background
        const stale = dataCache.getStaleData(dataCache.cacheKeys.TCG_STATS);
        if (stale && stale.isStale) {
            console.log('📦 Using stale TCG stats, revalidating in background...');
            this._revalidateTCGStats();
            return stale.data;
        }

        console.log('🔄 Fetching fresh TCG stats...');

        try {
            const response = await tcgAPI.getStatsBatch();
            const result = response.data.data;
            dataCache.setCacheData(dataCache.cacheKeys.TCG_STATS, result);
            return result;
        } catch (error) {
            // If fetch fails but we have stale data, use it
            if (stale) return stale.data;
            console.error('Failed to fetch TCG stats:', error);
            throw error;
        }
    }

    /** Background revalidation for TCG stats */
    _revalidateTCGStats() {
        tcgAPI.getStatsBatch()
            .then(response => {
                dataCache.setCacheData(dataCache.cacheKeys.TCG_STATS, response.data.data);
                console.log('✅ TCG stats revalidated');
            })
            .catch(err => console.error('Background revalidation failed:', err));
    }

    /**
     * Fetch sample cards for display
     * Uses cache if valid, otherwise fetches fresh data
     * Now uses batch endpoint (1 API call instead of N+1)
     */
    async getSampleCards(force = false) {
        if (!force) {
            const cached = dataCache.getCachedData(dataCache.cacheKeys.TCG_SAMPLES);
            if (cached) {
                console.log('📦 Using cached sample cards');
                return cached;
            }
        }

        const stale = dataCache.getStaleData(dataCache.cacheKeys.TCG_SAMPLES);
        if (stale && stale.isStale) {
            console.log('📦 Using stale sample cards, revalidating in background...');
            tcgAPI.getSamplesBatch({limit: 6})
                .then(r => dataCache.setCacheData(dataCache.cacheKeys.TCG_SAMPLES, r.data.data))
                .catch(console.error);
            return stale.data;
        }

        console.log('🔄 Fetching fresh sample cards...');

        try {
            const response = await tcgAPI.getSamplesBatch({ limit: 6 });
            const result = response.data.data;
            dataCache.setCacheData(dataCache.cacheKeys.TCG_SAMPLES, result);
            return result;
        } catch (error) {
            if (stale) return stale.data;
            console.error('Failed to fetch sample cards:', error);
            throw error;
        }
    }

    /**
     * Fetch API documentation endpoints
     * Uses cache if valid, otherwise fetches fresh data
     */
    async getDocEndpoints(force = false) {
        if (!force) {
            const cached = dataCache.getCachedData(dataCache.cacheKeys.DOC_ENDPOINTS);
            if (cached) {
                console.log('📦 Using cached documentation endpoints');
                return cached;
            }
        }

        const stale = dataCache.getStaleData(dataCache.cacheKeys.DOC_ENDPOINTS);
        if (stale && stale.isStale) {
            console.log('📦 Using stale doc endpoints, revalidating in background...');
            this._revalidateDocEndpoints();
            return stale.data;
        }

        console.log('🔄 Fetching fresh documentation endpoints...');

        try {
            const response = await publicAPI.getEndpoints();
            const result = {
                endpoints: response.data.data.endpoints,
                supportedTcgs: response.data.data.supported_tcgs,
                fetchedAt: new Date().toISOString()
            };
            dataCache.setCacheData(dataCache.cacheKeys.DOC_ENDPOINTS, result);
            return result;
        } catch (error) {
            if (stale) return stale.data;
            console.error('Failed to fetch documentation endpoints:', error);
            throw error;
        }
    }

    _revalidateDocEndpoints() {
        publicAPI.getEndpoints()
            .then(response => {
                const result = {
                    endpoints: response.data.data.endpoints,
                    supportedTcgs: response.data.data.supported_tcgs,
                    fetchedAt: new Date().toISOString()
                };
                dataCache.setCacheData(dataCache.cacheKeys.DOC_ENDPOINTS, result);
                console.log('✅ Doc endpoints revalidated');
            })
            .catch(console.error);
    }

    /**
     * Fetch API examples
     * Uses cache if valid, otherwise fetches fresh data
     */
    async getDocExamples(force = false) {
        if (!force) {
            const cached = dataCache.getCachedData(dataCache.cacheKeys.DOC_EXAMPLES);
            if (cached) {
                console.log('📦 Using cached API examples');
                return cached;
            }
        }

        const stale = dataCache.getStaleData(dataCache.cacheKeys.DOC_EXAMPLES);
        if (stale && stale.isStale) {
            console.log('📦 Using stale API examples, revalidating in background...');
            this._revalidateDocExamples();
            return stale.data;
        }

        console.log('🔄 Fetching fresh API examples...');

        try {
            const response = await publicAPI.getExamples();
            const result = {
                examples: response.data.data,
                fetchedAt: new Date().toISOString()
            };
            dataCache.setCacheData(dataCache.cacheKeys.DOC_EXAMPLES, result);
            return result;
        } catch (error) {
            if (stale) return stale.data;
            console.error('Failed to fetch API examples:', error);
            throw error;
        }
    }

    _revalidateDocExamples() {
        publicAPI.getExamples()
            .then(response => {
                const result = {
                    examples: response.data.data,
                    fetchedAt: new Date().toISOString()
                };
                dataCache.setCacheData(dataCache.cacheKeys.DOC_EXAMPLES, result);
                console.log('✅ Doc examples revalidated');
            })
            .catch(console.error);
    }

    /**
     * Get cache status information
     */
    getCacheStatus() {
        return dataCache.getCacheStatus();
    }

    /**
     * Clear all cached data
     */
    clearCache() {
        dataCache.clearCache();
    }

    /**
     * Schedule automatic revalidation
     * Returns cleanup function to cancel the interval
     */
    scheduleRevalidation(callback) {
        const TWELVE_HOURS = 12 * 60 * 60 * 1000;

        // Check if we need to revalidate now
        if (!dataCache.isCacheValid()) {
            this.getTCGStats(true).catch(console.error);
            this.getSampleCards(true).catch(console.error);
        }

        // Set up interval for automatic revalidation
        const intervalId = setInterval(() => {
            console.log('🔄 Auto-revalidating TCG data (12-hour cycle)...');
            this.getTCGStats(true)
                .then(() => this.getSampleCards(true))
                .then(() => {
                    if (callback) callback();
                })
                .catch(console.error);
        }, TWELVE_HOURS);

        // Return cleanup function
        return () => clearInterval(intervalId);
    }
}

const tcgDataService = new TCGDataService();

export default tcgDataService;
